import { helpContactSupportPath } from '@components/modules/help';
import { Icon } from '@components/ui/atoms/Icons';
import { useIsMerchant, useIsPlatform } from '@shared/hooks/useOrganizationType';
import { MENU_PERMISSIONS, usePermissionCheck } from '@shared/permissions';
import {
  Bank,
  Building,
  CardEdit,
  Code,
  Information,
  Messages,
  NotificationStatus,
  People,
  Profile2User,
  Setting2,
  Shop,
  Triangle,
  UserOctagon,
} from 'iconsax-reactjs';

import type { SidebarMenuItem } from '../types';

const USER_GUIDE_URL =
  'https://stonly.com/guide/en/merchant-user-guide-a8WDey6NS5/Steps/introduction';

export const useSystemMenuStructure = (): SidebarMenuItem[] => {
  const { hasAny } = usePermissionCheck();
  const isMerchant = useIsMerchant();
  const isPlatform = useIsPlatform();

  const menuItems: SidebarMenuItem[] = [
    ...(isMerchant
      ? [
          {
            icon: (type: 'active' | 'inactive') => renderIcon(type, Building),
            label: 'Business Profile',
            id: 'businessProfile',
            url: '/system/business-profile',
            type: 'itemGroup' as const,
            children: [],
          },
        ]
      : []),
    {
      icon: (type: 'active' | 'inactive') => renderIcon(type, People),
      label: 'User Management',
      id: 'userManagement',
      type: 'itemGroup' as const,
      children: [
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, Profile2User),
          label: 'Users',
          id: 'users',
          url: '/system/users',
          type: 'item' as const,
        },
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, UserOctagon),
          label: 'Roles',
          id: 'roles',
          url: '/system/roles',
          type: 'item' as const,
        },
      ],
    },
    {
      icon: (type: 'active' | 'inactive') => renderIcon(type, Shop),
      label: 'Merchants',
      id: 'merchants',
      url: '/system/merchants',
      type: 'itemGroup' as const,
      children: [],
    },
    // Banks are managed by the platform itself, so the entry is hidden for
    // merchant and bank organizations regardless of their permissions.
    ...(isPlatform
      ? [
          {
            icon: (type: 'active' | 'inactive') => renderIcon(type, Bank),
            label: 'Banks',
            id: 'banks',
            url: '/system/banks',
            type: 'itemGroup' as const,
            children: [],
          },
        ]
      : []),
    {
      icon: (type: 'active' | 'inactive') => renderIcon(type, Setting2),
      label: 'Settings',
      id: 'settings',
      type: 'itemGroup' as const,
      children: [
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, Code),
          url: '/system/developers',
          label: 'Developers',
          id: 'developers',
          type: 'item' as const,
        },
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, Triangle),
          label: 'Webhooks',
          id: 'webhooks',
          url: '/system/webhooks',
          type: 'item' as const,
        },
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, CardEdit),
          label: 'Gateway Settings',
          id: 'gatewaySettings',
          url: '/system/gateways',
          type: 'item' as const,
        },
      ],
    },
    {
      icon: (type: 'active' | 'inactive') => renderIcon(type, Information),
      label: 'Help',
      id: 'help',
      type: 'itemGroup' as const,
      children: [
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, NotificationStatus),
          label: 'User Guide',
          id: 'userGuide',
          type: 'item' as const,
          onClick: () => {
            window.open(USER_GUIDE_URL, '_blank', 'noopener,noreferrer');
          },
        },
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, Messages),
          label: 'Contact Support',
          id: 'contactSupport',
          type: 'item' as const,
          url: helpContactSupportPath,
        },
      ],
    },
  ];

  return menuItems
    .map((menuItem) => {
      // Filter children first based on their permissions
      if (menuItem.children && menuItem.children.length > 0) {
        const filteredChildren = menuItem.children.filter((child) => {
          const required = MENU_PERMISSIONS[child.id];

          return !required || hasAny(required);
        });

        return { ...menuItem, children: filteredChildren };
      }

      return menuItem;
    })
    .filter((menuItem) => {
      // For parent menus with children: show only if at least one child is visible
      if (menuItem.children && menuItem.children.length > 0) {
        return menuItem.children.length > 0;
      }
      // For menus without children: check their own permission

      const required = MENU_PERMISSIONS[menuItem.id];

      return !required || hasAny(required);
    });
};

function renderIcon(
  type: 'active' | 'inactive',
  IconComponent: React.ComponentType<{
    size?: string | number;
    variant?: 'Bold' | 'Linear' | 'Outline' | 'Broken' | 'Bulk' | 'TwoTone';
    color?: string;
  }>
) {
  const variant = type === 'active' ? 'Bold' : 'Linear';

  return (
    <Icon color="body.dark" size="sm">
      <IconComponent size="20" variant={variant} />
    </Icon>
  );
}
